import { useFrontendLayoutSettings } from '@Contexts/settings/FrontendLayoutSettingsContext';
import { type EnumFrontendLayoutSetting } from '@Model';
import { type Column } from '@synqo/frontend-component-library';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDebounce, useLocalStorage } from 'react-use';
import { extractCustomSettings, reconcileColumnLayouts, type TableColumnSettings } from './tableLayoutUtils';

const EMPTY_RECORD = {} as const;

interface UseTableLayoutFromSettingsReturnType {
    columnSizing: Record<string, number>;
    columnVisibility: Record<string, boolean>;
    updateSizing: (newSizes: Record<string, number>) => void;
    updateVisibility: (newVisibilities: Record<string, boolean>) => void;
}

const STORAGE_KEY_NAMESPACE = 'synqo.table.settings.column';

function isEmptyRecord(record: Record<string, unknown>) {
    return Object.keys(record).length === 0;
}

export const useTableLayoutFromSettings = <T>(
    tableContext: EnumFrontendLayoutSetting,
    tableColumns: Column<T>[],
): UseTableLayoutFromSettingsReturnType => {
    const { getParsedSetting, updateSetting } = useFrontendLayoutSettings();
    const { data: remoteSettings, setting } = useMemo(
        () => getParsedSetting<TableColumnSettings>(tableContext),
        [getParsedSetting, tableContext],
    );

    const sizingKey = `${STORAGE_KEY_NAMESPACE}.sizing.${tableContext.toString()}`;
    const visibilityKey = `${STORAGE_KEY_NAMESPACE}.visibility.${tableContext.toString()}`;

    const [columnSizing, setColumnSizing, removeColumnSizing] = useLocalStorage<Record<string, number>>(
        sizingKey,
        EMPTY_RECORD,
    );
    const [columnVisibility, setColumnVisibility, removeColumnVisibility] = useLocalStorage<Record<string, boolean>>(
        visibilityKey,
        EMPTY_RECORD,
    );

    const updateColumnSizing = useCallback(
        (value: Record<string, number>) => {
            if (isEmptyRecord(value)) {
                removeColumnSizing();
            } else {
                setColumnSizing(value);
            }
        },
        [setColumnSizing, removeColumnSizing],
    );

    const updateColumnVisibility = useCallback(
        (value: Record<string, boolean>) => {
            if (isEmptyRecord(value)) {
                removeColumnVisibility();
            } else {
                setColumnVisibility(value);
            }
        },
        [setColumnVisibility, removeColumnVisibility],
    );

    const columnSizingRef = useRef(columnSizing);
    const columnVisibilityRef = useRef(columnVisibility);
    columnSizingRef.current = columnSizing;
    columnVisibilityRef.current = columnVisibility;

    useEffect(() => {
        const { columnSizing: reconciledSizing, columnVisibility: reconciledVisibility } = reconcileColumnLayouts(
            remoteSettings,
            tableColumns,
            columnSizingRef.current ?? EMPTY_RECORD,
            columnVisibilityRef.current ?? EMPTY_RECORD,
        );

        updateColumnSizing(reconciledSizing);
        updateColumnVisibility(reconciledVisibility);
    }, [remoteSettings, tableColumns, updateColumnSizing, updateColumnVisibility]);

    const saveToRemote = useCallback(
        (latestColumnSizing: Record<string, number>, latestColumnVisibility: Record<string, boolean>) => {
            if (!setting) return;

            const settingsToSave = extractCustomSettings(tableColumns, latestColumnSizing, latestColumnVisibility);
            const newValue = settingsToSave ? JSON.stringify(settingsToSave) : '';
            if (newValue === setting.value) return;

            updateSetting({ ...setting, value: newValue });
        },
        [setting, tableColumns, updateSetting],
    );

    useDebounce(
        () => {
            if (columnSizing && columnVisibility) {
                saveToRemote(columnSizing, columnVisibility);
            }
        },
        128,
        [columnSizing, columnVisibility, saveToRemote],
    );

    return {
        columnSizing: columnSizing ?? EMPTY_RECORD,
        columnVisibility: columnVisibility ?? EMPTY_RECORD,
        updateSizing: updateColumnSizing,
        updateVisibility: updateColumnVisibility,
    };
};
